// The BcfActivatedRoute class is a service that provides information about the active route.
// It provides the fragment, queryParams and params of the active route.
// It is used by the components to get the information about the active route.
import { provideDiTokenValue } from '@bcf-vanilla-ts-v1-shared/di/di-token';
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import { combineLatest, distinctUntilChanged, map, of, startWith, type Observable } from 'rxjs';
import { APP_ROUTE_CHAIN, resolveRouteChainData } from '../bcf-preload/route-preload';
import { provideBcfActivatedUrl, type BcfActivatedUrl } from './bcf-activated-url';
import type { BcfRouterContainer, BcfRoutesMemory } from './bcf-router-container';
import { provideBcfRouterContainer } from './bcf-router-container';
import { provideBcfRouterServerSession, type BcfRouterServerSession } from './bcf-router-server-session';
import type { HistoryState } from './history-state';
import { provideHistoryState } from './history-state';
import { extractQueryParamsFromString } from './pure-utils/extract-query-params-from-string';
import type { Params } from './types';

type RouteData = Record<string, any>;

export interface BcfActivatedRouteContract {
  get fragment(): Observable<string | undefined>;

  // The queryParams are the parameters of the url after the ?.
  get queryParams(): Observable<Params>;

  // The params are the parameters of the route.
  get params(): Observable<Params>;

  get data(): Observable<RouteData>;

  dataTyped<T = RouteData>(): Observable<T>;
}
export class BcfActivatedRoute implements BcfActivatedRouteContract {
  constructor(
    private _bcfRouterContainer: BcfRouterContainer,
    private _historyState: HistoryState,
    private _serverCurrentSession: BcfRouterServerSession,
    private _activatedUrl: BcfActivatedUrl
  ) {}

  // The fragment is the part of the url after the #.
  public get fragment(): Observable<string | undefined> {
    if (__RENDER_MODE__ === 'server') {
      return of(undefined);
    }
    return this._historyState.getState().pipe(
      map(() => (window.location.hash.length > 0 ? window.location.hash.slice(1) : undefined)),
      distinctUntilChanged()
    );
  }

  // The queryParams are the parameters of the url after the ?.
  public get queryParams(): Observable<Params> {
    if (__RENDER_MODE__ === 'server') {
      return of(extractQueryParamsFromString(this._serverCurrentSession.ssrSearchString));
    }

    return this._historyState.getState().pipe(
      map(() => window.location.search),
      distinctUntilChanged(),
      map(extractQueryParamsFromString)
    );
  }

  // The params are the parameters of the route.
  public get params(): Observable<Params> {
    return this._bcfRouterContainer.didChanged$.pipe(
      startWith(undefined),
      map(() => this._bcfRouterContainer.getMemory()),
      map((memory: BcfRoutesMemory[]) => memory[memory.length - 1].params)
    );
  }

  // Chain data (see ADR-0016) resolves top-down from the url, so it is available before any
  // child <bcf-router> has rendered; memory (bottom-up, filled per-render) wins on top of it,
  // which keeps routers the chain doesn't cover (KNOWN GAP: user-settings, cms subpages) working.
  public get data(): Observable<RouteData> {
    return combineLatest([this._chainData(), this._memoryData()]).pipe(
      map(([chainData, memoryData]: [RouteData, RouteData]) => ({ ...chainData, ...memoryData }))
    );
  }

  public dataTyped<T = RouteData>(): Observable<T> {
    return this.data as Observable<T>;
  }

  // Decision: see ADR-0016
  private _chainData(): Observable<RouteData> {
    return this._activatedUrl.url$.pipe(
      map((url: string) => resolveRouteChainData(provideDiTokenValue(APP_ROUTE_CHAIN), url))
    );
  }

  private _memoryData(): Observable<RouteData> {
    return this._bcfRouterContainer.didChanged$.pipe(
      startWith(undefined),
      map(() => this._bcfRouterContainer.getMemory()),
      map((memory: BcfRoutesMemory[]) => {
        let container: RouteData = {};
        for (const item of memory) {
          if (!item.data) {
            continue;
          }
          container = {
            ...container,
            ...item.data
          };
        }
        return container;
      })
    );
  }
}

export function provideBcfActivatedRoute(): BcfActivatedRoute {
  return provideSingleton(
    BcfActivatedRoute,
    () =>
      new BcfActivatedRoute(
        provideBcfRouterContainer(),
        provideHistoryState(),
        provideBcfRouterServerSession(),
        provideBcfActivatedUrl()
      )
  );
}
